// umlaut-check: skip — Variablen-/Funktionsnamen ("neueZeile", "naechsterSchluessel"
// usw.) sind technische Bezeichner; echte Umlaute stehen in den sichtbaren
// Texten (Labels, Tooltips) korrekt.

import React, {useRef, useState} from "react";
import {OverlayTrigger, Table, Tooltip} from "react-bootstrap";
import {PiCopySimple, PiTrash} from "react-icons/pi";
import KwSelect from "./KwSelect.jsx";
import AmpelSelect, {AmpelPunkt, AMPEL_LABEL} from "./AmpelSelect.jsx";
import ArtikelSuchfeld from "./ArtikelSuchfeld.jsx";
import Farbfeld from "./Farbfeld.jsx";
import SelectList from "../SelectList.jsx";
import {formatFarbnummer} from "./format.js";

/**
 * Kurzes Artikel-Label "#016 signalweiß" aus Farbnummer/-name, sonst
 * Bezeichnung bzw. Artikelnummer als Rückfall (Sichtprüfung 1, Befund 9:
 * die volle b2b_article_master-Bezeichnung wie "PREMIUM 400ml Spray #231
 * signalweiß" wurde in der schmalen Spalte auf "…Spray #" abgeschnitten).
 * Farbnummer dreistellig mit führenden Nullen (#24-Rest, Sichtprüfung 2,
 * 22.09.2026: "#016" statt "#16").
 */
function kurzLabel(p) {
    if (p.farbnummer !== null && p.farbnummer !== undefined) {
        const nummer = formatFarbnummer(p.farbnummer, p.farbnummer_unter);

        return p.farbname ? `${nummer} ${p.farbname}` : nummer;
    }

    return p.bezeichnung || p.artikel_nummer || "";
}

const STATUS_OPTIONEN = [
    {value: "geplant", label: "Geplant"},
    {value: "bestaetigt", label: "Bestätigt"},
    {value: "kommissionierung", label: "Kommissionierung"},
    {value: "teilgeliefert", label: "Teilgeliefert"},
    {value: "geliefert", label: "Geliefert"},
    {value: "verschoben", label: "Verschoben"},
];

function Kopf({help, children, id}) {
    return (
        <th className="small">
            <OverlayTrigger placement="top" overlay={<Tooltip id={`${id}-th-tip`}>{help}</Tooltip>}>
                <span style={{cursor: "help", borderBottom: "1px dotted #999"}}>{children}</span>
            </OverlayTrigger>
        </th>
    );
}

let zaehler = 0;
function naechsterSchluessel() {
    zaehler += 1;
    return `neu-${Date.now()}-${zaehler}`;
}

/** Leere Entwurfszeile für "Neue Zeile"/"Enter in der letzten Zeile". */
export function neueLeereZeile() {
    return {
        _key: naechsterSchluessel(),
        id: null,
        artikel_nummer: "",
        artikel_bekannt: true,
        bezeichnung: "",
        farbnummer: null,
        farbnummer_unter: null,
        farbname: null,
        farbwert: null,
        cosmos_sku: null,
        marke: null,
        menge_wunsch: 0,
        menge_zusage: 0,
        menge_ist: 0,
        menge_vorschlag_neu: null,
        kw_wunsch: null,
        kw_zusage: null,
        kw_ist: null,
        status: "geplant",
        prioritaet: null,
        ampel: null,
        // Rückfallwert für eine frische, leere Zeile (Wunsch/Zusage/Ist
        // alle 0) - dieselbe Formel wie CosmosLieferposition::errechneteAmpel()
        // (N1, Rückmeldung 22.09.2026): "ampel" bleibt null, bis jemand
        // ausdrücklich wählt.
        ampel_errechnet: "rot",
        quelle: "hand",
        sicherheit: "geplant",
    };
}

/**
 * Tabellarische Erfassung der Cosmos-Lieferpositionen (AP5, Auftrag Punkt
 * 3): Direkteingabe je Zelle, Artikelsuche mit Vorschlagsliste, Tab
 * springt zellenweise (native Browser-Tab-Reihenfolge über die
 * Formularelemente - kein Zusatzcode nötig), Enter in der letzten Zeile
 * legt eine neue Zeile an, Zeilen duplizierbar/entfernbar. Speichern
 * sammelt die Zeilen im Elternteil (LieferungDetail.jsx) in einem
 * Request.
 */
export default function LieferPositionenTabelle({positionen, onChange, canEdit}) {
    const [neueZeilenSchluessel, setNeueZeilenSchluessel] = useState(null);
    const tabelleRef = useRef(null);

    const aktualisiereZeile = (key, feld, wert) => {
        onChange(positionen.map((p) => (p._key === key ? {...p, [feld]: wert, _dirty: true} : p)));
    };

    const zeileHinzufuegen = () => {
        const zeile = neueLeereZeile();
        onChange([...positionen, zeile]);
        setNeueZeilenSchluessel(zeile._key);
    };

    const zeileDuplizieren = (key) => {
        const vorlage = positionen.find((p) => p._key === key);
        if (!vorlage) {
            return;
        }
        const kopie = {
            ...vorlage,
            _key: naechsterSchluessel(),
            id: null,
            menge_zusage: 0,
            menge_ist: 0,
            status: "geplant",
            _dirty: true,
        };
        const index = positionen.findIndex((p) => p._key === key);
        const neu = [...positionen.slice(0, index + 1), kopie, ...positionen.slice(index + 1)];
        onChange(neu);
        setNeueZeilenSchluessel(kopie._key);
    };

    const zeileEntfernen = (key) => {
        onChange(positionen.filter((p) => p._key !== key));
    };

    // Nach dem Hinzufügen/Duplizieren einer Zeile den Fokus dorthin holen -
    // ohne diesen Effekt würde man nach Enter bzw. "Duplizieren" weiterhin
    // im alten Feld stehen.
    React.useEffect(() => {
        if (!neueZeilenSchluessel) {
            return;
        }
        const ziel = tabelleRef.current?.querySelector(`[data-row-key="${neueZeilenSchluessel}"] input, [data-row-key="${neueZeilenSchluessel}"] button`);
        ziel?.focus();
        setNeueZeilenSchluessel(null);
    }, [neueZeilenSchluessel, positionen.length]);

    // Bewusst die Bubble-Phase (onKeyDown, nicht onKeyDownCapture): das
    // Artikel-Suchfeld ruft bei einer Vorschlagsauswahl
    // "e.stopPropagation()" auf einem Enter-Tastendruck auf (Bubble-Phase
    // des Zielelements feuert zuerst) - der Tastendruck erreicht diese
    // Zeilen-Behandlung dann gar nicht erst. Nur ein Enter aus den übrigen
    // Zellen (Mengen, Priorität) soll die neue Zeile anlegen.
    const handleZeileKeyDown = (e, index) => {
        if (!canEdit || e.key !== "Enter" || index !== positionen.length - 1) {
            return;
        }
        e.preventDefault();
        zeileHinzufuegen();
    };

    return (
        <div ref={tabelleRef} className="table-responsive">
            {/* "minWidth" auf der Tabelle selbst statt nur an den Zellen (Sichtprüfung 1,
                Befund 9): sonst schrumpft der Browser einzelne Spalten (u. a. "Ist" auf
                rund 20 px) unter ihre Mindestbreite, statt die Tabelle waagerecht
                scrollen zu lassen. #9/N5 (Sichtprüfung 2, 22.09.2026): Spaltenbreiten
                verdichtet, damit Ampel/Status/Priorität/Aktionen bei 1.440 px ohne
                waagerechtes Scrollen sichtbar bleiben. */}
            <Table bordered hover size="sm" className="align-middle bg-white" style={{minWidth: 1300}}>
                <thead className="table-light">
                    <tr>
                        <Kopf id="artikel" help="Farbnummer (z. B. 231 oder #231), Name oder Artikelnummer eintippen und aus der Liste wählen. Ohne Treffer bleibt der Text als freie Bezeichnung stehen.">
                            Artikel
                        </Kopf>
                        <Kopf id="wunsch" help="Von uns gewünschte Menge dieser Farbe in dieser Lieferung.">
                            Wunsch
                        </Kopf>
                        <Kopf id="zusage" help="Von Cosmos zugesagte Menge.">
                            Zusage
                        </Kopf>
                        <Kopf id="ist" help="Tatsächlich gelieferte Menge (aus Lieferschein/Wareneingang).">
                            Ist
                        </Kopf>
                        <Kopf id="vorschlag" help="Aus einer Verschiebung abgeleiteter neuer Vorschlag (Block „NEW - POSSIBLE ORDER“ der Cosmos-Übersicht).">
                            Vorschlag neu
                        </Kopf>
                        <Kopf id="kww" help="Kalenderwoche, in der wir diese Menge wünschen.">
                            KW Wunsch
                        </Kopf>
                        <Kopf id="kwz" help="Von Cosmos zugesagte Kalenderwoche.">
                            KW Zusage
                        </Kopf>
                        <Kopf id="kwi" help="Kalenderwoche der tatsächlichen Lieferung.">
                            KW Ist
                        </Kopf>
                        <Kopf id="ampel" help="Von Hand gesetzte Ampel. Ohne eigene Wahl zeigt die Zeile die errechnete Ampel (grau markiert).">
                            Ampel
                        </Kopf>
                        <Kopf id="status" help="Status dieser Position (unabhängig vom Status der gesamten Lieferung möglich).">
                            Status
                        </Kopf>
                        <Kopf id="prio" help="Priorität innerhalb des LKW, niedrigere Zahl = wichtiger.">
                            Prio
                        </Kopf>
                        <th className="small">Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    {positionen.map((p, index) => (
                        <tr key={p._key} data-row-key={p._key} onKeyDown={(e) => handleZeileKeyDown(e, index)}>
                            <td style={{minWidth: 210}}>
                                <div className="d-flex align-items-center gap-2">
                                    <Farbfeld farbwert={p.farbwert} size={20} />
                                    <div className="flex-grow-1">
                                        <ArtikelSuchfeld
                                            artikelNummer={p.artikel_nummer}
                                            anzeige={kurzLabel(p)}
                                            artikelBekannt={p.artikel_bekannt !== false}
                                            disabled={!canEdit}
                                            onWahl={(treffer) => {
                                                onChange(
                                                    positionen.map((z) =>
                                                        z._key === p._key
                                                            ? {
                                                                  ...z,
                                                                  artikel_nummer: treffer.artikel_nummer,
                                                                  bezeichnung: treffer.bezeichnung || treffer.farbname,
                                                                  farbnummer: treffer.farbnummer,
                                                                  farbnummer_unter: treffer.farbnummer_unter,
                                                                  farbname: treffer.farbname,
                                                                  farbwert: treffer.farbwert,
                                                                  marke: treffer.produktlinie,
                                                                  artikel_bekannt: true,
                                                                  _dirty: true,
                                                              }
                                                            : z
                                                    )
                                                );
                                            }}
                                            onFreitext={(text) => {
                                                onChange(
                                                    positionen.map((z) =>
                                                        z._key === p._key
                                                            ? {...z, artikel_nummer: text, bezeichnung: text, farbnummer: null, farbnummer_unter: null, farbname: null, artikel_bekannt: false, _dirty: true}
                                                            : z
                                                    )
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                                {p.artikel_nummer && <div className="small text-muted ps-4">{p.artikel_nummer}</div>}
                            </td>
                            <td style={{minWidth: 90}}>
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control form-control-sm"
                                    value={p.menge_wunsch ?? 0}
                                    disabled={!canEdit}
                                    onChange={(e) => aktualisiereZeile(p._key, "menge_wunsch", Number(e.target.value))}
                                />
                            </td>
                            <td style={{minWidth: 90}}>
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control form-control-sm"
                                    value={p.menge_zusage ?? 0}
                                    disabled={!canEdit}
                                    onChange={(e) => aktualisiereZeile(p._key, "menge_zusage", Number(e.target.value))}
                                />
                            </td>
                            <td style={{minWidth: 90}}>
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control form-control-sm"
                                    value={p.menge_ist ?? 0}
                                    disabled={!canEdit}
                                    onChange={(e) => aktualisiereZeile(p._key, "menge_ist", Number(e.target.value))}
                                />
                            </td>
                            <td style={{minWidth: 95}}>
                                <input
                                    type="number"
                                    min="0"
                                    className="form-control form-control-sm"
                                    value={p.menge_vorschlag_neu ?? ""}
                                    disabled={!canEdit}
                                    onChange={(e) => aktualisiereZeile(p._key, "menge_vorschlag_neu", e.target.value === "" ? null : Number(e.target.value))}
                                />
                            </td>
                            <td style={{minWidth: 105}}>
                                <KwSelect id={`kww-${p._key}`} value={p.kw_wunsch} onChange={(v) => aktualisiereZeile(p._key, "kw_wunsch", v)} disabled={!canEdit} ariaLabel="Kalenderwoche Wunsch" />
                            </td>
                            <td style={{minWidth: 105}}>
                                <KwSelect id={`kwz-${p._key}`} value={p.kw_zusage} onChange={(v) => aktualisiereZeile(p._key, "kw_zusage", v)} disabled={!canEdit} ariaLabel="Kalenderwoche Zusage" />
                            </td>
                            <td style={{minWidth: 105}}>
                                <KwSelect id={`kwi-${p._key}`} value={p.kw_ist} onChange={(v) => aktualisiereZeile(p._key, "kw_ist", v)} disabled={!canEdit} ariaLabel="Kalenderwoche Ist" />
                            </td>
                            <td style={{minWidth: 130}}>
                                {canEdit ? (
                                    <>
                                        <AmpelSelect id={`ampel-${p._key}`} value={p.ampel} onChange={(v) => aktualisiereZeile(p._key, "ampel", v)} ariaLabel="Ampel" />
                                        {/* N1 (Rückmeldung 22.09.2026): nur ANZEIGEN, nie zurückschicken -
                                            "ampel" bleibt null, solange niemand ausdrücklich wählt, sonst
                                            würde die errechnete Ampel beim nächsten Speichern als "von Hand"
                                            gesetzt eingefroren. */}
                                        {!p.ampel && p.ampel_errechnet && (
                                            <div className="small text-muted d-flex align-items-center gap-1 mt-1">
                                                <AmpelPunkt farbe={p.ampel_errechnet} /> errechnet: {AMPEL_LABEL[p.ampel_errechnet] || p.ampel_errechnet}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="d-inline-flex align-items-center gap-1">
                                        <AmpelPunkt farbe={p.ampel ?? p.ampel_errechnet} /> {!p.ampel ? <span className="text-muted fst-italic">errechnet</span> : null}
                                    </span>
                                )}
                            </td>
                            <td style={{minWidth: 130}}>
                                <SelectList id={`status-${p._key}`} value={p.status || "geplant"} options={STATUS_OPTIONEN} onChange={(v) => aktualisiereZeile(p._key, "status", v)} size="sm" disabled={!canEdit} ariaLabel="Status der Position" />
                            </td>
                            <td style={{minWidth: 65}}>
                                <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    value={p.prioritaet ?? ""}
                                    disabled={!canEdit}
                                    onChange={(e) => aktualisiereZeile(p._key, "prioritaet", e.target.value === "" ? null : Number(e.target.value))}
                                />
                            </td>
                            <td style={{minWidth: 85, whiteSpace: "nowrap"}}>
                                {canEdit && (
                                    <>
                                        <OverlayTrigger placement="top" overlay={<Tooltip id={`dup-${p._key}`}>Legt eine Kopie dieser Zeile mit Zusage/Ist auf 0 direkt darunter an.</Tooltip>}>
                                            <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => zeileDuplizieren(p._key)} aria-label="Zeile duplizieren">
                                                <PiCopySimple />
                                            </button>
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="top" overlay={<Tooltip id={`del-${p._key}`}>Entfernt diese Position. Bereits gespeicherte Positionen werden beim nächsten Speichern protokolliert (Änderungsart „entfallen“).</Tooltip>}>
                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => zeileEntfernen(p._key)} aria-label="Zeile entfernen">
                                                <PiTrash />
                                            </button>
                                        </OverlayTrigger>
                                    </>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            {canEdit && (
                <OverlayTrigger placement="top" overlay={<Tooltip id="neue-position-tip">Fügt eine leere Zeile an - dasselbe passiert automatisch bei Enter in der letzten Zeile.</Tooltip>}>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={zeileHinzufuegen}>
                        + Neue Position
                    </button>
                </OverlayTrigger>
            )}
        </div>
    );
}
